import { PermissionCode, type IntranetContact } from '@gb-ticket/shared-types';
import { ExternalLinkIcon, Loader2Icon, PencilIcon, PhoneIcon } from 'lucide-react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePageTitle } from '@/hooks/use-page-title';
import { useVoice } from '@/platform/voice/use-voice';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import {
  ColleagueNameButton,
  colleagueVisibleName,
} from '@/products/intranet/components/colleague-name-button';
import { ColleaguePortrait } from '@/products/intranet/features/intranet/colleague-portrait';
import { useIntranetContacts } from '@/products/intranet/features/intranet/use-intranet-directory';
import { INTRANET_EDITORIAL_CONTACTS_PATH } from '@/products/intranet/intranet-paths';

export function ContactsPage() {
  usePageTitle('Ansprechpartner');
  const me = useMe();
  const contacts = useIntranetContacts();
  const items = contacts.data?.items ?? [];
  const canManage = hasPermission(me.data, PermissionCode.INTRANET_CONTACTS_MANAGE);

  return (
    <section className="min-w-0 space-y-6">
      <PlatformPageHeader
        title="Ansprechpartner"
        description="Wer für welche Aufgabe zuständig ist."
        action={
          canManage ? (
            <Button asChild size="sm" className="w-full sm:w-auto">
              <Link to={INTRANET_EDITORIAL_CONTACTS_PATH}>
                <PencilIcon aria-hidden />
                Bearbeiten
              </Link>
            </Button>
          ) : null
        }
      />

      {contacts.isPending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Ansprechpartner werden geladen …
        </p>
      ) : contacts.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Die Ansprechpartner konnten nicht geladen werden.
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Noch keine Ansprechpartner hinterlegt.</p>
      ) : (
        <ul className="grid min-w-0 gap-3">
          {items.map((contact) => (
            <li key={contact.id} className="min-w-0">
              <ContactCard contact={contact} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ContactCard({ contact }: { contact: IntranetContact }) {
  const voice = useVoice();
  return (
    <Card className="min-w-0 overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="truncate text-base">{contact.functionTitle}</CardTitle>
      </CardHeader>
      <CardContent className="min-w-0 space-y-1 text-sm text-muted-foreground">
        {contact.user ? (
          <ColleagueNameButton
            userId={contact.user.id}
            displayName={contact.user.displayName}
            className="flex min-w-0 items-center gap-3 text-left font-normal hover:no-underline"
          >
            <ColleaguePortrait
              displayName={contact.user.displayName}
              profilePhotoUrl={contact.user.profilePhotoUrl ?? null}
              className="size-10 text-xs"
            />
            <span className="min-w-0 truncate text-sm text-muted-foreground">
              <span className="font-medium text-foreground">
                {colleagueVisibleName(contact.user.displayName, voice)}
              </span>
              {contact.user.jobTitle ? ` · ${contact.user.jobTitle}` : ''}
            </span>
          </ColleagueNameButton>
        ) : null}
        {contact.department ? <p className="truncate">{contact.department.name}</p> : null}
        {contact.user?.phone ? (
          <p className="flex min-w-0 items-center gap-1.5 text-foreground">
            <PhoneIcon className="size-3.5 shrink-0" aria-hidden />
            <a href={`tel:${contact.user.phone}`} className="truncate hover:underline">
              {contact.user.phone}
            </a>
          </p>
        ) : null}
        {contact.externalUrl ? (
          <p className="min-w-0">
            <a
              href={contact.externalUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex max-w-full items-center gap-1 text-foreground hover:underline"
            >
              <ExternalLinkIcon className="size-3.5 shrink-0" aria-hidden />
              <span className="truncate">Externer Link</span>
            </a>
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
